<script lang="ts" setup>
import type { IntegrationLoginProvider, MfaEnrolment, SocialLoginProvider, UnifiedAuthStep } from '~kui/types/auth';

import { AuthFlowLayout, PasswordForgotAction } from '~kui/types/auth';
import { AnimatePresence, motion } from 'motion-v';

interface AuthState {
  email: string;
  firstName: string;
  lastName: string;
}

interface CountryState {
  label: string;
  value: string;
}

/**
 * - `helpText`: a line under the email step's heading, for a door that explains who signs in there.
 * - `disclaimer`: sanitised HTML shown under the card, such as the Terms and Privacy links.
 * - `showSocial`: false hides the social and integration sign-in buttons on the email step.
 * - `stepImages`: per-step overrides for the image that sits on the card's corner.
 * - `layout`: `public` fills the viewport under the public nav and lifts the card above centre;
 *   `embedded` lets the card sit in the flow of a page shell that already owns the height.
 * - `mfaSignedIn`: a session already exists, so the MFA steps offer to sign out of it.
 * - `passwordSetupDescription`: the password-setup step's own line under its heading; empty keeps the step's default.
 */
interface Props {
  currentStep: UnifiedAuthStep;
  authState: AuthState;
  isLoading: boolean;
  isSubmitting: boolean;
  globalError: string | null;
  direction: 'forward' | 'backward';
  countryStates: CountryState[];
  initialOtpCode?: string;
  initialEmail?: string;
  emailLocked?: boolean;
  /**
   * Whether the consumer is ready to accept a signup. The registration steps do more than collect
   * fields — submitting one is the account being created — and the consumer knows things about
   * whether that call can succeed that this component cannot see. False holds those steps inert
   * rather than letting a submit go out that can only be refused.
   */
  signupReady?: boolean;
  /** Href for the terms page, resolved by the consumer (carries the cluster path prefix). */
  termsPath?: string;
  integrationProviders?: string[];
  passwordForgotAction?: PasswordForgotAction;
  helpText?: string;
  disclaimer?: string;
  showSocial?: boolean;
  stepImages?: Partial<Record<UnifiedAuthStep, string>>;
  layout?: AuthFlowLayout;
  mfaExpired?: boolean;
  mfaSignedIn?: boolean;
  mfaEnrolment?: MfaEnrolment | null;
  mfaLoadFailed?: boolean;
  mfaRecoveryCodes?: string[] | null;
  registerDisabledHelpText?: string;
  passwordSetupDescription?: string;
}

const props = withDefaults(defineProps<Props>(), {
  initialOtpCode: '',
  initialEmail: '',
  emailLocked: false,
  signupReady: true,
  termsPath: '/terms',
  integrationProviders: () => [],
  passwordForgotAction: PasswordForgotAction.Code,
  helpText: '',
  disclaimer: '',
  showSocial: true,
  stepImages: () => ({}),
  layout: AuthFlowLayout.Public,
  mfaExpired: false,
  mfaSignedIn: false,
  mfaEnrolment: null,
  mfaLoadFailed: false,
  mfaRecoveryCodes: null,
  registerDisabledHelpText: '',
  passwordSetupDescription: '',
});

const emit = defineEmits<{
  emailSubmit: [email: string];
  socialLogin: [provider: SocialLoginProvider | IntegrationLoginProvider];
  methodChooseOtp: [];
  methodChoosePassword: [];
  methodBack: [];
  passwordSubmit: [password: string];
  passwordForgot: [];
  passwordBack: [];
  otpSubmit: [code: string];
  otpResend: [onComplete: () => void];
  otpBack: [];
  consentSubmit: [];
  nameSubmit: [firstName: string, lastName: string];
  detailsSubmit: [stateId: string, birthDate: Date];
  detailsBack: [];
  passwordSetupSubmit: [password: string];
  passwordSetupSkip: [];
  welcomeContinue: [];
  mfaSubmitCode: [code: string];
  mfaSubmitRecoveryCode: [code: string];
  mfaStartAgain: [];
  mfaSignOut: [];
  mfaEnrolRetry: [];
  mfaEnrolSubmitCode: [code: string];
  mfaRecoveryCodesSaved: [];
  registerDisabledBack: [];
}>();

const emailRef = ref<{ setError: (message: string) => void } | null>(null);
const otpRef = ref<{ clearAndReset: () => void; setError: (message: string) => void } | null>(null);
const passwordRef = ref<{ setError: (message: string) => void } | null>(null);
const passwordSetupRef = ref<{ setError: (message: string) => void } | null>(null);
const mfaChallengeRef = ref<{ setError: (message: string) => void } | null>(null);
const mfaEnrolRef = ref<{ setError: (message: string) => void } | null>(null);

const clearOtp = () => otpRef.value?.clearAndReset();
const setEmailError = (message: string) => emailRef.value?.setError(message);
const setOtpError = (message: string) => otpRef.value?.setError(message);
const setPasswordError = (message: string) => passwordRef.value?.setError(message);
const setPasswordSetupError = (message: string) => passwordSetupRef.value?.setError(message);
const setMfaChallengeError = (message: string) => mfaChallengeRef.value?.setError(message);
const setMfaEnrolError = (message: string) => mfaEnrolRef.value?.setError(message);

defineExpose({ clearOtp, setEmailError, setOtpError, setPasswordError, setPasswordSetupError, setMfaChallengeError, setMfaEnrolError });

const stepIconMap: Record<UnifiedAuthStep, string | null> = {
  'email': '/images/auth/rocket.webp',
  'method': '/images/auth/envelope.webp',
  'password': '/images/auth/rocket.webp',
  'otp': '/images/auth/envelope.webp',
  'choose-account': '/images/auth/rocket.webp',
  'school': '/images/auth/flame.webp',
  'consent': '/images/auth/flame.webp',
  'name': '/images/auth/flame.webp',
  'details': '/images/auth/flame.webp',
  'password-setup': '/images/auth/rocket.webp',
  'welcome': null,
  'mfa-enrol': '/images/coach/padlock.webp',
  'mfa-challenge': '/images/coach/padlock.webp',
  'register-disabled': '/images/coach/padlock.webp',
};

const stepIcon = computed(() => props.stepImages[props.currentStep] ?? stepIconMap[props.currentStep] ?? null);

const loaderVariants = cva('', {
  variants: {
    layout: {
      [AuthFlowLayout.Public]: 'h-dvh',
      [AuthFlowLayout.Embedded]: 'py-16',
    },
  },
});

const containerVariants = cva('relative z-10 flex flex-1 flex-col items-center justify-center px-5', {
  variants: {
    layout: {
      [AuthFlowLayout.Public]: 'h-[calc(100dvh-42px)] lg:h-[calc(100dvh-56px)]',
      [AuthFlowLayout.Embedded]: '',
    },
  },
});

const columnVariants = cva('w-full max-w-sm', {
  variants: {
    layout: {
      [AuthFlowLayout.Public]: '-translate-y-[10vh]',
      [AuthFlowLayout.Embedded]: '',
    },
  },
});

const loaderClass = computed(() => twMerge(loaderVariants({ layout: props.layout })));
const containerClass = computed(() => twMerge(containerVariants({ layout: props.layout })));
const columnClass = computed(() => twMerge(columnVariants({ layout: props.layout })));

const stepRef = ref<{ $el?: HTMLElement } | null>(null);

/**
 * `AnimatePresence` mounts each step fresh, so focus follows the wrapper appearing rather than
 * the step prop changing — under `mode="wait"` the outgoing step is still on screen when
 * `currentStep` moves. Steps that carry a hidden `autocomplete="username"` field for password
 * managers put it first in the DOM, and it is not somewhere focus can usefully land, so only
 * tabbable controls count.
 */
watch(stepRef, (step) => {
  step?.$el?.querySelector<HTMLInputElement>('input:not([tabindex="-1"]), select, textarea')?.focus();
}, { flush: 'post' });
</script>

<template>
  <div>
    <!-- Public layout: full viewport height, as the public nav is hidden while the post-login redirect runs. -->
    <div
      v-if="isLoading"
      :class="loaderClass"
      data-testid="auth-flow-loading"
    >
      <KUICoreContainerCentered>
        <KUICoreLoaderAnyway />
      </KUICoreContainerCentered>
    </div>
    <div
      v-else
      :class="containerClass"
      data-testid="auth-flow"
    >
      <div :class="columnClass" data-testid="auth-flow-column">
        <div class="relative w-full rounded-4xl bg-white py-7 px-8 shadow-sm">
          <div class="absolute -top-13 right-0 size-24 md:-right-10">
            <AnimatePresence mode="popLayout">
              <motion.div
                v-if="stepIcon"
                :key="stepIcon"
                :initial="{ scale: 0, opacity: 0 }"
                :animate="{ scale: 1, opacity: 1 }"
                :exit="{ scale: 0, opacity: 0 }"
                :transition="{ duration: 0.2, type: 'spring' }"
              >
                <NuxtImg
                  :src="stepIcon"
                  class="size-32 object-scale-down"
                  role="presentation"
                  alt=""
                />
              </motion.div>
            </AnimatePresence>
          </div>

          <Transition
            enter-active-class="transition-all duration-300"
            enter-from-class="opacity-0 -translate-y-2"
            leave-active-class="transition-all duration-200"
            leave-to-class="opacity-0 -translate-y-2"
          >
            <div v-if="globalError" class="mb-6 rounded-2xl border border-destructive bg-destructive-subtle px-4 py-3">
              <p class="text-xs text-destructive text-center font-medium">
                {{ globalError }}
              </p>
            </div>
          </Transition>

          <AnimatePresence mode="wait">
            <motion.div
              ref="stepRef"
              :key="currentStep"
              :initial="{ opacity: 0, x: direction === 'forward' ? 40 : -40 }"
              :animate="{ opacity: 1, x: 0 }"
              :exit="{ opacity: 0, x: direction === 'forward' ? -40 : 40 }"
              :transition="{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }"
            >
              <KUIUnifiedAuthStepEmail
                v-if="currentStep === 'email'"
                ref="emailRef"
                :loading="isSubmitting"
                :initial-email="initialEmail"
                :email-locked="emailLocked"
                :integration-providers="integrationProviders"
                :show-social="showSocial"
                :help-text="helpText"
                @submit="(email) => emit('emailSubmit', email)"
                @social-login="(provider) => emit('socialLogin', provider)"
              />

              <KUIUnifiedAuthStepMethod
                v-else-if="currentStep === 'method'"
                :email="authState.email"
                :loading="isSubmitting"
                @choose-otp="emit('methodChooseOtp')"
                @choose-password="emit('methodChoosePassword')"
                @back="emit('methodBack')"
              />

              <KUIUnifiedAuthStepPassword
                v-else-if="currentStep === 'password'"
                ref="passwordRef"
                :email="authState.email"
                :loading="isSubmitting"
                :forgot-action="passwordForgotAction"
                @submit="(password) => emit('passwordSubmit', password)"
                @forgot="emit('passwordForgot')"
                @back="emit('passwordBack')"
              />

              <KUIUnifiedAuthStepOtp
                v-else-if="currentStep === 'otp'"
                ref="otpRef"
                :email="authState.email"
                :loading="isSubmitting"
                :initial-code="initialOtpCode"
                @submit="(code) => emit('otpSubmit', code)"
                @back="emit('otpBack')"
                @resend="(onComplete) => emit('otpResend', onComplete)"
              />

              <slot
                v-else-if="currentStep === 'choose-account'"
                name="choose-account"
              />

              <slot
                v-else-if="currentStep === 'school'"
                name="school"
              />

              <KUIUnifiedAuthStepConsent
                v-else-if="currentStep === 'consent'"
                :loading="isSubmitting || !signupReady"
                :terms-path="termsPath"
                @submit="emit('consentSubmit')"
              />

              <KUIUnifiedAuthStepName
                v-else-if="currentStep === 'name'"
                :initial-first-name="authState.firstName"
                :initial-last-name="authState.lastName"
                :loading="isSubmitting"
                @submit="(fn, ln) => emit('nameSubmit', fn, ln)"
              />

              <Suspense v-else-if="currentStep === 'details'">
                <KUIUnifiedAuthStepDetails
                  :first-name="authState.firstName"
                  :country-states="countryStates"
                  :loading="isSubmitting || !signupReady"
                  @submit="(stateId, birthDate) => emit('detailsSubmit', stateId, birthDate)"
                  @back="emit('detailsBack')"
                />
                <template #fallback>
                  <div class="flex justify-center py-8">
                    <KUICoreLoaderAnyway />
                  </div>
                </template>
              </Suspense>

              <KUIUnifiedAuthStepPasswordSetup
                v-else-if="currentStep === 'password-setup'"
                ref="passwordSetupRef"
                :email="authState.email"
                :loading="isSubmitting"
                :description="passwordSetupDescription || undefined"
                @submit="(password) => emit('passwordSetupSubmit', password)"
                @skip="emit('passwordSetupSkip')"
              />

              <KUIUnifiedAuthStepWelcome
                v-else-if="currentStep === 'welcome'"
                :first-name="authState.firstName"
                @continue="emit('welcomeContinue')"
              />

              <KUIUnifiedAuthStepMfaChallenge
                v-else-if="currentStep === 'mfa-challenge'"
                ref="mfaChallengeRef"
                :loading="isSubmitting"
                :expired="mfaExpired"
                :signed-in="mfaSignedIn"
                @submit-code="(code) => emit('mfaSubmitCode', code)"
                @submit-recovery-code="(code) => emit('mfaSubmitRecoveryCode', code)"
                @start-again="emit('mfaStartAgain')"
                @sign-out="emit('mfaSignOut')"
              />

              <KUIUnifiedAuthStepMfaEnrol
                v-else-if="currentStep === 'mfa-enrol'"
                ref="mfaEnrolRef"
                :loading="isSubmitting"
                :expired="mfaExpired"
                :signed-in="mfaSignedIn"
                :enrolment="mfaEnrolment"
                :load-failed="mfaLoadFailed"
                :recovery-codes="mfaRecoveryCodes"
                @retry="emit('mfaEnrolRetry')"
                @submit-code="(code) => emit('mfaEnrolSubmitCode', code)"
                @saved="emit('mfaRecoveryCodesSaved')"
                @start-again="emit('mfaStartAgain')"
                @sign-out="emit('mfaSignOut')"
              />

              <KUIUnifiedAuthStepRegisterDisabled
                v-else-if="currentStep === 'register-disabled'"
                :help-text="registerDisabledHelpText"
                @back="emit('registerDisabledBack')"
              />
            </motion.div>
          </AnimatePresence>
          <!-- Entry-step only: a flow-switching prompt makes no sense mid-verification
               or on the post-login steps, and contradicts the register-disabled copy. -->
          <slot
            v-if="currentStep === 'email'"
            name="footer"
          />
        </div>
        <p
          v-if="disclaimer"
          class="mt-6 text-center text-xs text-muted-foreground"
          data-testid="auth-flow-disclaimer"
        >
          <KUICoreHtml as="span" :html="disclaimer" />
        </p>
      </div>
    </div>
  </div>
</template>
